import { useState, useEffect } from 'react'
import { ArrowRight, Check, Eye, EyeOff, KeyRound, Lock, ShieldAlert, X } from 'lucide-react'
import { MriButton, MriInput, MriModal } from '@mriqbox/ui-kit'

export interface PinModalProps {
    open: boolean
    roomCode: string
    roomName?: string
    error?: string | null
    onClose: () => void
    onSubmit: (pin: string, remember: boolean) => void
}

export function PinModal({
    open,
    roomCode,
    roomName,
    error,
    onClose,
    onSubmit
}: PinModalProps) {
    const [pin, setPin] = useState('')
    const [remember, setRemember] = useState(true)
    const [showPassword, setShowPassword] = useState(false)

    useEffect(() => {
        if (open) {
            setPin('')
            setShowPassword(false)
        }
    }, [open])

    if (!open) return null

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        if (!pin.trim()) return
        onSubmit(pin.trim(), remember)
    }

    return (
        // MriModal ja resolve role="dialog", aria-modal, fechar com Esc e
        // prender o foco (Radix por baixo) — este e o unico modal do produto e
        // aparece justamente pra quem esta tentando entrar numa sala, entao
        // reescrever isso na mao deixaria buraco de acessibilidade.
        <MriModal onClose={onClose} className="gap-0 overflow-hidden rounded-2xl border-amber-500/30 select-none">
            {/* Fechar modal */}
            <button
                type="button"
                onClick={onClose}
                aria-label="Fechar"
                className="absolute right-4 top-4 rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            >
                <X className="h-4 w-4" />
            </button>

            {/* Ícone e Cabeçalho */}
            <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-500/15 border border-amber-500/30 text-amber-400">
                    <Lock className="h-5 w-5" />
                </div>
                <div>
                    <h3 className="text-base font-bold text-foreground">Sala Protegida</h3>
                    <p className="text-xs text-muted-foreground">
                        {roomName ? (
                            <>
                                <span className="font-semibold text-foreground">{roomName}</span> ({roomCode})
                            </>
                        ) : (
                            <>Código: <span className="font-mono font-semibold text-foreground">{roomCode}</span></>
                        )}
                    </p>
                </div>
            </div>

            <p className="mt-3 text-xs text-muted-foreground leading-relaxed">
                Esta sala exige um PIN de acesso definido pelo criador para autorizar a sua entrada.
            </p>

            {/* Formulário */}
            <form onSubmit={handleSubmit} className="mt-4 space-y-3.5">
                <div>
                    <label htmlFor="pin-input" className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground block mb-1">
                        PIN da Sala
                    </label>
                    <div className="relative">
                        <span className="absolute inset-y-0 left-2.5 flex items-center text-amber-400">
                            <KeyRound className="h-4 w-4" />
                        </span>
                        <MriInput
                            id="pin-input"
                            type={showPassword ? 'text' : 'password'}
                            value={pin}
                            maxLength={12}
                            autoFocus
                            placeholder="Digite a senha de 4 a 12 dígitos"
                            className="pl-9 pr-9 text-sm font-medium tracking-wide"
                            onChange={(e) => setPin(e.target.value)}
                        />
                        <button
                            type="button"
                            tabIndex={-1}
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute inset-y-0 right-2.5 flex items-center text-muted-foreground hover:text-foreground"
                        >
                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                    </div>
                </div>

                {/* Checkbox de Lembrar Senha */}
                <div className="rounded-xl border border-border/70 bg-background/50 p-2.5">
                    <label className="flex items-center justify-between cursor-pointer">
                        <div className="pr-2">
                            <p className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                                <Check className="h-3.5 w-3.5 text-primary" />
                                Lembrar senha neste dispositivo
                            </p>
                            {/* Explicito de proposito: o PIN fica em texto puro no
                                localStorage — quem marca precisa saber onde ele vai parar. */}
                            <p className="text-[10px] text-muted-foreground mt-0.5">
                                Fica salvo sem criptografia neste navegador — qualquer pessoa que usar
                                este aparelho entra direto, sem digitar nada
                            </p>
                        </div>
                        <input
                            type="checkbox"
                            checked={remember}
                            onChange={(e) => setRemember(e.target.checked)}
                            className="h-4 w-4 rounded border-border accent-primary cursor-pointer shrink-0"
                        />
                    </label>
                </div>

                {error && (
                    <div className="flex items-center gap-2 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                        <ShieldAlert className="h-4 w-4 shrink-0" />
                        <span>{error}</span>
                    </div>
                )}

                <div className="flex gap-2 pt-1">
                    <MriButton
                        type="button"
                        variant="ghost"
                        className="flex-1"
                        onClick={onClose}
                    >
                        Cancelar
                    </MriButton>
                    <MriButton
                        type="submit"
                        className="flex-1 font-semibold bg-amber-500 text-black hover:bg-amber-400 gap-1.5"
                        disabled={!pin.trim()}
                    >
                        Entrar na Sala
                        <ArrowRight className="h-4 w-4" />
                    </MriButton>
                </div>
            </form>
        </MriModal>
    )
}
